import { BaseWebComponentController, IListenerConfig } from '../../../framework/BaseWebComponentController';
import { CloudWorkloadSecurityCustomerViewWebComponent } from '../component/CloudWorkloadSecurityCustomerViewWebComponent';
import { CloudWorkloadSecurityCustomerViewView } from '../view/CloudWorkloadSecurityCustomerViewView';
import { template } from '../view/template';
import { CloudWorkloadSecurityCustomerViewRepository } from '../repository/CloudWorkloadSecurityCustomerViewRepository';
import { CloudWorkloadSecurityCompanyRecord } from '../types';
import { showErrorToast } from '@nirvana/shared/common';
import { translateFn } from '../../../utils/translator.helper';

export class CloudWorkloadSecurityCustomerViewController extends BaseWebComponentController {
  public component: CloudWorkloadSecurityCustomerViewWebComponent;
  public view!: CloudWorkloadSecurityCustomerViewView;
  public viewConstructor: typeof CloudWorkloadSecurityCustomerViewView;
  public dataRepository: CloudWorkloadSecurityCustomerViewRepository;
  public company: CloudWorkloadSecurityCompanyRecord | undefined;

  constructor(component: CloudWorkloadSecurityCustomerViewWebComponent) {
    super(component);

    this.initialSetup({
      viewConstructor: CloudWorkloadSecurityCustomerViewView,
      viewTemplate: template,
      dataRepositoryConstructor: CloudWorkloadSecurityCustomerViewRepository,
    });
  }

  computeEventListeners(): IListenerConfig[] {
    return [
      {
        element: this.view.dom.$createAccountButton,
        event: 'click',
        callback: () => this.view.showCreateAccountDialog(this.company, () => this.createAccount()),
      },
      {
        element: this.view.dom.$openConsoleButton,
        event: 'click',
        callback: this.openCloudWorkloadSecurityConsole,
      },
    ];
  }

  async refresh() {
    this.view.setLoadingState();
    await this.render();
  }

  private async createAccount() {
    if (!this.company) {
      this.view.showCreateAccountErrorToast();
      return;
    }

    try {
      const result = await this.dataRepository.createAccount(this.company._id);
      if (!result.success) {
        this.view.showCreateAccountErrorToast();
        return;
      }

      this.view.showCreateAccountSuccessToast();
      await this.refresh();
    } catch (e) {
      this.view.showCreateAccountErrorToast(e);
    }
  }

  private async openCloudWorkloadSecurityConsole() {
    if (!this.company) {
      console.error('Cannot open console due to unknown company details.');
      return;
    }

    this.view.dom.$openConsoleButton.disabled = true;
    this.view.dom.$openConsoleButton.showSpinner = true;

    try {
      await this.openConsoleForCurrentUserAndCompany(this.company._id);
    } catch (error) {
      showErrorToast(error);
    }

    this.view.dom.$openConsoleButton.disabled = false;
    this.view.dom.$openConsoleButton.showSpinner = false;
  }

  async openConsoleForCurrentUserAndCompany(companyId: string): Promise<void> {
    const errorMessage = translateFn('Unexpected error occurred while opening the Cloud Security console.');
    let response;

    try {
      response = await this.dependencies.api.clients.CloudWorkloadSecurity.openConsole(companyId).toPromise();
    } catch (error) {
      throw new Error(errorMessage);
    }

    if (!response || !response.success) {
      throw new Error(errorMessage);
    }
  }

  async getDataForView(): Promise<CloudWorkloadSecurityCompanyRecord | undefined> {
    this.company = await this.dataRepository.getCompany();
    return this.company;
  }

  dispatchEvent(type: string, company: CloudWorkloadSecurityCompanyRecord | undefined) {
    if (company === undefined) {
      return;
    }

    this.view.dom.$wrapper.dispatchEvent(
      new CustomEvent(type, {
        detail: {
          id: company._id,
          name: company.companyName,
          fromCustomerView: true,
        },
        bubbles: true,
        composed: true,
      })
    );
  }
}
